/**
 * Wordmark, composed in pure SVG using Fraunces text so it scales perfectly
 * and respects the brand tokens. The decorative underline mark is rendered
 * via SVG path for now; once the gpt-image-2 underline asset is generated and
 * imported, we'll swap the <path> for a <use href="..." /> reference to it.
 *
 * Variants:
 *   variant="primary", ink text, tan underline (default)
 *   variant="mono", single color, no underline
 *   variant="inverse", cream text, tan underline (for ink backgrounds)
 */

import { type CSSProperties } from 'react';

type Variant = 'primary' | 'mono' | 'inverse';

interface Props {
  variant?: Variant;
  className?: string;
  title?: string;
  height?: number | string;
}

export function Wordmark({
  variant = 'primary',
  className = '',
  title = 'Your Lane Coaching',
  height = 40,
}: Props) {
  const textColor =
    variant === 'inverse' ? 'var(--color-cream)' : 'var(--color-ink)';
  const underlineColor =
    variant === 'mono' ? 'transparent' : 'var(--color-tan)';

  const style: CSSProperties = {
    height: typeof height === 'number' ? `${height}px` : height,
    width: 'auto',
    display: 'block',
  };

  return (
    <svg
      role="img"
      aria-label={title}
      viewBox="0 0 360 80"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <title>{title}</title>
      <text
        x="0"
        y="46"
        fontFamily="'Fraunces Variable', 'Fraunces', 'Cormorant Garamond', Georgia, serif"
        fontWeight={500}
        fontSize="40"
        fill={textColor}
        letterSpacing="-0.5"
      >
        Your Lane
      </text>
      <text
        x="0"
        y="68"
        fontFamily="'Inter Variable', 'Inter', system-ui, sans-serif"
        fontWeight={600}
        fontSize="11"
        fill={textColor}
        opacity={0.7}
        letterSpacing="3"
        style={{ textTransform: 'uppercase' } as CSSProperties}
      >
        COACHING
      </text>
      {/* Decorative underline under the word "Lane" */}
      <path
        d="M 96 52 C 120 49, 156 50, 196 53"
        fill="none"
        stroke={underlineColor}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
